<script lang="ts">
export default {
  inheritAttrs: false,
}
</script>

<script setup lang="ts">
import { ref, computed, useSlots, useAttrs, watch } from "vue"

const props = withDefaults(
  defineProps<{
    value?: number
    hideTabTitles?: boolean
    hideTabContent?: boolean
    disabled?: boolean
    backgroundColor?: string
    dark?: boolean
    grow?: boolean
    textColor?: string
    subheading?: boolean
  }>(),
  {
    backgroundColor: "transparent",
    textColor: "primary",
  },
)

const emit = defineEmits<{
  (event: "input", value: number): void
}>()

const selectedTab = ref(props.value ?? 0)
const slots = useSlots()
const attrs = useAttrs()

watch(
  () => selectedTab.value,
  newValue => {
    emit("input", newValue)
  },
)

watch(
  () => props.value,
  newValue => {
    selectedTab.value = newValue ?? 0
  },
)

const tabs = computed(() => {
  const defaultSlot = slots.default?.()
  if (!defaultSlot) return []

  return defaultSlot
    .filter(vnode => vnode.componentOptions?.tag === "Tab")
    .map(vnode => {
      const componentPropsData = vnode.componentOptions?.propsData as {
        name: string
        icon: string
        to?: any
        count?: number
      }
      return {
        name: componentPropsData.name ?? "",
        icon: componentPropsData.icon ?? "",
        to: componentPropsData.to ?? undefined,
        count: componentPropsData.count ?? 0,
      }
    })
})
</script>

<template>
  <div style="display: contents">
    <v-tabs
      v-if="!hideTabTitles"
      v-model="selectedTab"
      v-bind="attrs"
      fixed-tabs
      :background-color="backgroundColor"
      :color="textColor"
      :disabled="disabled"
      :dark="dark"
      :grow="grow"
    >
      <v-tab
        v-for="{ name, icon, to, count } in tabs"
        :key="name"
        :to="to"
        :class="{ subheading }"
      >
        <v-icon v-if="icon">{{ icon }}</v-icon>
        {{ name }}
        <v-avatar v-if="count" left class="primary ml-2" for size="25">
          <span class="white--text">
            {{ count }}
          </span>
        </v-avatar>
      </v-tab>
    </v-tabs>
    <v-tabs-items v-if="!hideTabContent" v-model="selectedTab">
      <slot />
    </v-tabs-items>
  </div>
</template>
